/**
 * Precision Planning — Yield Forecasting  (mvp2_agrotech_precision)
 *
 * Uses satellite NDVI time-series data (already stored by the Temporal Analysis
 * pipeline) and treatment history to produce a seasonal yield performance
 * summary per field.  The AI model is deterministic and transparent: the
 * projection is computed from:
 *   • Current-season mean NDVI vs. long-run baseline (all historical imagery)
 *   • NDVI recovery trend after last treatment (if available)
 *   • Seasonal anomaly penalty (count × severity weight)
 *
 * Data sources (all existing endpoints):
 *  • GET /api/satellite/boundaries          — field list
 *  • GET /api/satellite-imagery/:id/records — NDVI time series
 *  • GET /api/anomalies                     — anomaly history per field
 *  • GET /api/missions                      — completed remediation history
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Minus, RefreshCw,
  Activity, AlertTriangle, BarChart2, ZapOff,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import {
  getBoundaries, getAllAnomalies, getAllMissions, getBatchedImagery, invalidateCache,
  type CachedField, type CachedAnomaly, type CachedImageryRecord, type CachedMission,
} from '../../services/precisionDataCache';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ─────────────────────────────────────────────────────────────────────

type Field         = CachedField;
type ImageryRecord = CachedImageryRecord;

interface ForecastRecord {
  field: Field;
  currentSeasonNdvi: number | null;
  baselineNdvi: number | null;
  ndviDelta: number | null;
  performancePct: number | null;   // vs baseline
  anomalyPenalty: number;          // 0–30 %
  projectedYieldIndex: number | null;  // 0–100
  trend: 'improving' | 'declining' | 'stable' | 'no_data';
  lastUpdated: string | null;
  anomalyCount: number;
  remediationCount: number;
  ndviSeries: { date: string; ndvi: number }[];
}

// ─── Projection helpers ────────────────────────────────────────────────────────

const SEVERITY_WEIGHT: Record<string, number> = {
  low: 2, medium: 5, high: 10, critical: 15,
};

function computeForecast(
  field: Field,
  imagery: ImageryRecord[],
  anomalies: any[],
  remediationCount: number,
): ForecastRecord {
  const ndviSeries = imagery
    .map(r => ({
      date: r.acquisition_date.slice(0, 10),
      ndvi: r.indices.find(i => i.index_type === 'NDVI')?.mean_value ?? NaN,
    }))
    .filter(x => !isNaN(x.ndvi))
    .sort((a, b) => a.date.localeCompare(b.date));

  if (ndviSeries.length === 0) {
    return {
      field, currentSeasonNdvi: null, baselineNdvi: null, ndviDelta: null,
      performancePct: null, anomalyPenalty: 0, projectedYieldIndex: null,
      trend: 'no_data', lastUpdated: null,
      anomalyCount: anomalies.length, remediationCount, ndviSeries: [],
    };
  }

  // Baseline = mean of all records
  const baseline = ndviSeries.reduce((s, r) => s + r.ndvi, 0) / ndviSeries.length;

  // Current season = mean of last 3 records (≈ 30-day window)
  const recent = ndviSeries.slice(-3);
  const currentNdvi = recent.reduce((s, r) => s + r.ndvi, 0) / recent.length;

  const delta = currentNdvi - baseline;
  const performancePct = +((delta / baseline) * 100).toFixed(1);

  // Anomaly penalty — weight by severity, cap at 30 %
  const penalty = Math.min(
    30,
    anomalies
      .filter(a => a.status !== 'resolved' && a.status !== 'false_positive')
      .reduce((s: number, a: any) => s + (SEVERITY_WEIGHT[a.severity] ?? 5), 0)
  );

  // Projected yield index (0–100): scaled NDVI with anomaly penalty and remediation boost
  const remBoost = Math.min(5, remediationCount * 2);   // up to +5 from treatments
  const rawIdx = Math.max(0, Math.min(100, (currentNdvi / 0.8) * 100 - penalty + remBoost));
  const projectedYieldIndex = +rawIdx.toFixed(1);

  let trend: ForecastRecord['trend'] = 'stable';
  if (ndviSeries.length >= 2) {
    const prev = ndviSeries[ndviSeries.length - 2].ndvi;
    const curr = ndviSeries[ndviSeries.length - 1].ndvi;
    if (curr - prev > 0.02) trend = 'improving';
    else if (prev - curr > 0.02) trend = 'declining';
  }

  return {
    field, currentSeasonNdvi: +currentNdvi.toFixed(3), baselineNdvi: +baseline.toFixed(3),
    ndviDelta: +delta.toFixed(3), performancePct, anomalyPenalty: +penalty.toFixed(1),
    projectedYieldIndex, trend,
    lastUpdated: ndviSeries[ndviSeries.length - 1]?.date ?? null,
    anomalyCount: anomalies.length, remediationCount, ndviSeries,
  };
}

// ─── Mini sparkline (SVG) ──────────────────────────────────────────────────────

function Sparkline({ series }: { series: { date: string; ndvi: number }[] }) {
  if (series.length < 2) return <span className="text-xs text-gray-400">—</span>;
  const vals = series.map(s => s.ndvi);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 0.01;
  const W = 120; const H = 36; const pad = 3;
  const points = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v - min) / range) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const lastVal = vals[vals.length - 1];
  const colour = lastVal >= 0.55 ? '#16a34a' : lastVal >= 0.35 ? '#d97706' : '#dc2626';
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="shrink-0">
      <polyline points={points} fill="none" stroke={colour} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Trend icon ────────────────────────────────────────────────────────────────

function TrendIcon({ trend }: { trend: ForecastRecord['trend'] }) {
  if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (trend === 'declining') return <TrendingDown className="w-4 h-4 text-red-500" />;
  if (trend === 'stable')    return <Minus className="w-4 h-4 text-gray-400" />;
  return <Activity className="w-4 h-4 text-gray-300" />;
}

// ─── Yield index bar ───────────────────────────────────────────────────────────

function YieldIndexBar({ score }: { score: number | null }) {
  if (score === null) return <span className="text-xs text-gray-400">No data</span>;
  const colour = score >= 70 ? 'bg-green-500' : score >= 45 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700">
        <div className={`h-2 rounded-full ${colour}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-bold text-gray-700 dark:text-gray-300 w-9 text-right">{score}</span>
    </div>
  );
}

// ─── Forecast card ─────────────────────────────────────────────────────────────

function ForecastCard({ rec }: { rec: ForecastRecord }) {
  const { uom } = useOrgSettings();
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-base font-bold text-gray-900 dark:text-white">{rec.field.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {rec.field.area_hectares?.toFixed(1) ?? '—'} {uom.area_unit_label}
              {rec.field.metadata?.crop_type && ` · ${rec.field.metadata.crop_type}`}
              {rec.lastUpdated && ` · Last imagery: ${rec.lastUpdated}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <TrendIcon trend={rec.trend} />
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
              rec.trend === 'improving' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
              rec.trend === 'declining' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
              rec.trend === 'stable'    ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' :
              'bg-gray-100 text-gray-400'
            }`}>
              {rec.trend === 'no_data' ? 'No data' : rec.trend.charAt(0).toUpperCase() + rec.trend.slice(1)}
            </span>
          </div>
        </div>

        {/* Yield index */}
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Projected Yield Index</p>
          <YieldIndexBar score={rec.projectedYieldIndex} />
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Current NDVI</p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{rec.currentSeasonNdvi ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Baseline NDVI</p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{rec.baselineNdvi ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">vs Baseline</p>
            <p className={`text-sm font-bold ${rec.performancePct !== null && rec.performancePct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {rec.performancePct !== null ? `${rec.performancePct >= 0 ? '+' : ''}${rec.performancePct}%` : '—'}
            </p>
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Anomaly Penalty</p>
            <p className="text-sm font-bold text-amber-600 dark:text-amber-400">
              {rec.anomalyPenalty > 0 ? `-${rec.anomalyPenalty}%` : 'None'}
            </p>
          </div>
        </div>

        {/* Sparkline row */}
        {rec.ndviSeries.length >= 2 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">NDVI trend</p>
            <Sparkline series={rec.ndviSeries} />
          </div>
        )}

        {/* Expand for audit factors */}
        {rec.projectedYieldIndex !== null && (
          <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
            <button type="button" onClick={() => setExpanded(v => !v)}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1">
              {expanded ? '▲ Hide factors' : '▼ Show projection factors'}
            </button>
            {expanded && (
              <div className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                <p>• NDVI data points used: <strong>{rec.ndviSeries.length}</strong></p>
                <p>• Active anomalies (unresolved): <strong>{rec.anomalyCount}</strong></p>
                <p>• Remediation treatments applied: <strong>{rec.remediationCount}</strong></p>
                <p>• Remediation boost: <strong>+{Math.min(5, rec.remediationCount * 2)}%</strong></p>
                <p className="text-gray-400 italic">
                  Yield index = (mean NDVI / 0.8 × 100) − anomaly penalty + remediation boost
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export const YieldForecastingPage: React.FC = () => {
  const [forecasts, setForecasts] = useState<ForecastRecord[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  const load = useCallback(async (bust = false) => {
    try {
      setLoading(true);
      setError(null);
      if (bust) invalidateCache();

      // All three base datasets share the module-level TTL cache across pages
      const [fields, allAnomalies, allMissions] = await Promise.all([
        getBoundaries(),
        getAllAnomalies().catch(() => [] as CachedAnomaly[]),
        getAllMissions().catch(() => [] as CachedMission[]),
      ]);

      // Group anomalies by boundary — zero extra requests
      const anomalyMap = new Map<string, CachedAnomaly[]>();
      allAnomalies.forEach(a => {
        if (a.boundary_id) {
          if (!anomalyMap.has(a.boundary_id)) anomalyMap.set(a.boundary_id, []);
          anomalyMap.get(a.boundary_id)!.push(a);
        }
      });

      // Imagery via shared cache — batched + inter-batch delay
      const imageryMap = await getBatchedImagery(fields, 50);

      const results: ForecastRecord[] = fields.map(field => {
        const imagery: ImageryRecord[] = imageryMap.get(field.id) ?? [];
        const anomalies = anomalyMap.get(field.id) ?? [];
        const remediationCount = allMissions.filter(
          m => m.mission_type === 'remediation' && m.status === 'completed'
            && (m.metadata?.boundary_id === field.id || m.metadata?.field_id === field.id)
        ).length;
        return computeForecast(field, imagery, anomalies, remediationCount);
      });

      results.sort((a, b) => {
        const order = { declining: 0, no_data: 1, stable: 2, improving: 3 };
        return (order[a.trend] ?? 1) - (order[b.trend] ?? 1);
      });

      setForecasts(results);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load forecast data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const improvingCount = forecasts.filter(f => f.trend === 'improving').length;
  const decliningCount = forecasts.filter(f => f.trend === 'declining').length;
  const avgYield = forecasts.filter(f => f.projectedYieldIndex !== null).reduce((s, f, _, arr) => s + (f.projectedYieldIndex ?? 0) / arr.length, 0);

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-600" /> Yield Forecasting
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            AI-powered seasonal yield performance index derived from satellite NDVI, anomaly history, and treatment outcomes
          </p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Summary ────────────────────────────────────────────────────────── */}
      {!loading && forecasts.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Fields',     value: forecasts.length,  colour: 'text-gray-700 dark:text-gray-300' },
            { label: 'Improving',        value: improvingCount,    colour: 'text-green-600' },
            { label: 'Declining',        value: decliningCount,    colour: 'text-red-600' },
            { label: 'Avg Yield Index',  value: forecasts.some(f => f.projectedYieldIndex !== null) ? avgYield.toFixed(1) : '—', colour: avgYield >= 70 ? 'text-green-600' : avgYield >= 45 ? 'text-amber-600' : 'text-red-600' },
          ].map(({ label, value, colour }) => (
            <Card key={label} className="p-4 text-center">
              <p className={`text-2xl font-bold ${colour}`}>{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Methodology note ───────────────────────────────────────────────── */}
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/40 p-4 flex gap-3">
        <BarChart2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
          <strong>Yield Index Methodology:</strong> Satellite NDVI time-series mean vs. long-run field baseline,
          adjusted for active anomaly severity (penalty up to −30%) and confirmed remediation treatments (boost up to +5%).
          Index range 0–100; ≥70 = high potential, 45–69 = moderate, &lt;45 = at-risk. Not a substitute for agronomist assessment.
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : forecasts.length === 0 ? (
        <Card className="p-10 text-center">
          <ZapOff className="mx-auto w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No fields found</p>
          <p className="text-xs text-gray-400 mt-1">Define fields and run temporal analysis to generate yield forecasts.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {forecasts.map(rec => <ForecastCard key={rec.field.id} rec={rec} />)}
        </div>
      )}
    </div>
  );
};

export default YieldForecastingPage;
// Made with Bob
